import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Building,
  Users,
  TrendingUp,
  Target,
  AlertTriangle,
  DollarSign,
  Calendar,
  FileText,
  Plus,
  Save,
  Upload,
  Brain
} from 'lucide-react';
import { demoCompanies } from '../../data/demoData';
import { Company } from '../../types';

const CompanyResearch = () => {
  const [selectedCompany, setSelectedCompany] = useState<Company>(demoCompanies[0]);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<Company>>(selectedCompany);

  const handleInputChange = (field: keyof Company, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayInputChange = (field: keyof Company, value: string, index?: number) => {
    if (typeof formData[field] === 'object' && Array.isArray(formData[field])) {
      const currentArray = [...(formData[field] as string[])];
      if (index !== undefined) {
        currentArray[index] = value;
      } else {
        currentArray.push(value);
      }
      setFormData(prev => ({ ...prev, [field]: currentArray }));
    }
  };

  const removeArrayItem = (field: keyof Company, index: number) => {
    if (typeof formData[field] === 'object' && Array.isArray(formData[field])) {
      const currentArray = [...(formData[field] as string[])];
      currentArray.splice(index, 1);
      setFormData(prev => ({ ...prev, [field]: currentArray }));
    }
  };

  const saveChanges = () => {
    setSelectedCompany(formData as Company);
    setIsEditing(false);
  };

  const industryOptions = ['SaaS', 'Financial Technology', 'E-commerce', 'Healthcare', 'Education', 'Manufacturing', 'Retail'];
  const sizeOptions = ['startup', 'small', 'medium', 'large', 'enterprise'];

  return (
    <div className="h-full bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 p-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold gradient-text mb-2">Company Research & Analysis</h1>
              <p className="text-slate-600 dark:text-slate-400">
                Comprehensive company profiling for strategic roadmap development
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary flex items-center space-x-2"
              >
                <Upload className="w-4 h-4" />
                <span>Import Data</span>
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsEditing(!isEditing)}
                className="btn-primary flex items-center space-x-2"
              >
                <FileText className="w-4 h-4" />
                <span>{isEditing ? 'View Mode' : 'Edit Mode'}</span>
              </motion.button>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
              Select Company
            </h3>
            
            <div className="space-y-3">
              {demoCompanies.map((company) => (
                <motion.div
                  key={company.id}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => {
                    setSelectedCompany(company);
                    setFormData(company);
                  }}
                  className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                    selectedCompany.id === company.id
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500'
                      : 'bg-slate-50 dark:bg-slate-800 border-2 border-transparent hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                      <Building className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-slate-900 dark:text-slate-100 text-sm">
                        {company.name}
                      </div>
                      <div className="text-xs text-slate-600 dark:text-slate-400">
                        {company.industry}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-secondary w-full mt-4 flex items-center justify-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Add New</span>
            </motion.button>
          </div>

          <div className="lg:col-span-3 space-y-6">
            <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                  Company Profile
                </h3>
                {isEditing && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={saveChanges}
                    className="btn-primary flex items-center space-x-2"
                  >
                    <Save className="w-4 h-4" />
                    <span>Save Changes</span>
                  </motion.button>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Company Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="input-field"
                    />
                  ) : (
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="font-medium text-slate-900 dark:text-slate-100">
                        {selectedCompany.name}
                      </div>
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Industry
                  </label>
                  {isEditing ? (
                    <select
                      value={formData.industry || ''}
                      onChange={(e) => handleInputChange('industry', e.target.value)}
                      className="input-field"
                    >
                      {industryOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="font-medium text-slate-900 dark:text-slate-100">
                        {selectedCompany.industry}
                      </div>
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Company Size
                  </label>
                  {isEditing ? (
                    <select
                      value={formData.size || ''}
                      onChange={(e) => handleInputChange('size', e.target.value)}
                      className="input-field"
                    >
                      {sizeOptions.map(option => (
                        <option key={option} value={option}>
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="font-medium text-slate-900 dark:text-slate-100 capitalize">
                        {selectedCompany.size}
                      </div>
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Budget
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.budget || ''}
                      onChange={(e) => handleInputChange('budget', e.target.value)}
                      className="input-field"
                    />
                  ) : (
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="font-medium text-slate-900 dark:text-slate-100">
                        {selectedCompany.budget}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mt-6">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Description
                </label>
                {isEditing ? (
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="input-field"
                  />
                ) : (
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="text-slate-900 dark:text-slate-100">
                      {selectedCompany.description}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    Current Challenges
                  </h3>
                </div>
                
                <div className="space-y-3">
                  {(formData.currentChallenges || []).map((challenge, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-start space-x-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg"
                    >
                      <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                      {isEditing ? (
                        <div className="flex-1 flex items-center space-x-2">
                          <input
                            type="text"
                            value={challenge}
                            onChange={(e) => handleArrayInputChange('currentChallenges', e.target.value, index)}
                            className="flex-1 p-2 border border-slate-300 rounded-lg text-sm"
                          />
                          <button
                            onClick={() => removeArrayItem('currentChallenges', index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <span className="text-sm text-slate-700 dark:text-slate-300">
                          {challenge}
                        </span>
                      )}
                    </motion.div>
                  ))}
                  
                  {isEditing && (
                    <button
                      onClick={() => handleArrayInputChange('currentChallenges', '')}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                    >
                      + Add Challenge
                    </button>
                  )}
                </div>
              </div>

              <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Target className="w-5 h-5 text-purple-500" />
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    Competitors
                  </h3>
                </div>
                
                <div className="space-y-3">
                  {(formData.competitors || []).map((competitor, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center space-x-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg"
                    >
                      <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                        <Building className="w-4 h-4 text-white" />
                      </div>
                      {isEditing ? (
                        <div className="flex-1 flex items-center space-x-2">
                          <input
                            type="text"
                            value={competitor}
                            onChange={(e) => handleArrayInputChange('competitors', e.target.value, index)}
                            className="flex-1 p-2 border border-slate-300 rounded-lg text-sm"
                          />
                          <button
                            onClick={() => removeArrayItem('competitors', index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <span className="font-medium text-slate-900 dark:text-slate-100">
                          {competitor}
                        </span>
                      )}
                    </motion.div>
                  ))}
                  
                  {isEditing && (
                    <button
                      onClick={() => handleArrayInputChange('competitors', '')}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                    >
                      + Add Competitor
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Technology Stack
                </h3>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {(formData.techStack || []).map((tech, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center space-x-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700"
                  >
                    {isEditing ? (
                      <>
                        <input
                          type="text"
                          value={tech}
                          onChange={(e) => handleArrayInputChange('techStack', e.target.value, index)}
                          className="bg-transparent border-none p-0 text-sm font-medium text-blue-800 dark:text-blue-300 focus:outline-none"
                        />
                        <button
                          onClick={() => removeArrayItem('techStack', index)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          ×
                        </button>
                      </>
                    ) : (
                      <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                        {tech}
                      </span>
                    )}
                  </motion.div>
                ))}
                
                {isEditing && (
                  <button
                    onClick={() => handleArrayInputChange('techStack', '')}
                    className="px-3 py-2 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                  >
                    + Add Technology
                  </button>
                )}
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-2xl border border-green-200/50 dark:border-green-700/50 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-xl flex items-center justify-center">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      Ready for AI Analysis
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Company profile is complete and ready for TRIZ-powered roadmap generation
                    </p>
                  </div>
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary bg-gradient-to-r from-green-500 to-blue-500 border-0"
                >
                  <Brain className="w-4 h-4 mr-2" />
                  Start AI Analysis
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyResearch;